import React, { useEffect, useState } from "react";
import { Input, Button, Modal, Upload, Select } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import {
  useAllOrders,
  useRtoAccept,
  useRtoReject,
} from "../../../../api/orders/orders";
import { useLoader } from "../../../../utils/LoaderContext";

const ORDERS_PER_PAGE = 20;

const RTO_REJECT_REASONS = [
  "Wrong item delivered",
  "Item damaged",
  "Item expired",
  "Customer refused to accept",
  "Customer not available",
  "Incorrect address",
  "Other",
];

const RtoStatus = ({ status = "rtodelivered", rtoStatus = "" }) => {
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectRemark, setRejectRemark] = useState("");
  const [rejectFile, setRejectFile] = useState(null);
  const [rejectOrderId, setRejectOrderId] = useState(null);

  const { setLoading } = useLoader();

  const {
    data: allOrdersData,
    isLoading: isAllLoading,
    isError,
  } = useAllOrders({
    currentPage,
    ORDERS_PER_PAGE,
    status,
    rtoStatus,
    search: searchQuery,
  });

  const { mutate: acceptOrder } = useRtoAccept();
  const { mutate: rejectOrder } = useRtoReject();

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearchQuery(searchText.trim());
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchText]);

  const orders = allOrdersData?.orders || [];
  const isLoading = isAllLoading;

  const handleAccept = (awb) => acceptOrder(awb);
  
  const handleRejectOpen = (awb) => {
    setRejectOrderId(awb);
    setRejectModalOpen(true);
  };
  
  const handleRejectConfirm = () => {
    if (!rejectRemark.trim()) return;

    const payload = { 
      orderId: rejectOrderId, 
      remark: rejectRemark,
      photo: rejectFile ? rejectFile : null
    };
    rejectOrder(payload);

    setRejectModalOpen(false);
    setRejectRemark("");
    setRejectFile(null);
    setRejectOrderId(null);
  };

  const handleModalClose = () => {
    setRejectModalOpen(false);
    setRejectRemark("");
    setRejectFile(null);
    setRejectOrderId(null);
  };

  if (isError)
    return <p className="text-red-600 mt-4">Error fetching orders</p>;

  return (
    <div className="w-full mt-6">
      {/* Search */}
      <div className="mb-4 flex justify-between items-center">
        <Input
          className="w-80"
          placeholder="Search by AWB, Order Number or Name"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto hide-scrollbar">
        <table className="min-w-[1500px] w-full table-auto border border-gray-200">
          <thead className="bg-gray-100 text-left text-sm font-semibold text-gray-700">
            <tr>
              <th className="p-3">Order Date</th>
              <th className="p-3">Order Number</th>
              <th className="p-3">AWB / Courier</th>
              <th className="p-3">Payment / Invoice</th>
              <th className="p-3">Products</th>
              <th className="p-3">Customer / Contact</th>
              <th className="p-3">Shipping Address</th>
              <th className="p-3">RTO Info</th>
              {rtoStatus === "" && <th className="p-3">Actions</th>}
            </tr>
          </thead>

          <tbody className="bg-white text-sm">
            {isLoading ? (
              <tr>
                <td colSpan={11} className="text-center py-6 text-gray-500">
                  Loading orders...
                </td>
              </tr>
            ) : orders.length > 0 ? (
              orders.map((order, index) => (
                <tr key={order._id} className="hover:bg-gray-50">
                  {/* Order Date */}
                  <td className="p-4">
                    {new Date(order.createdAt).toLocaleDateString()}
                  </td>

                  {/* Manifest / Shipping Status */}
                  <td className="p-4">
                    <div className="font-medium text-blue-600">
                      {order.OrderNumber}
                    </div>
                    <div
                      className={`text-xs font-semibold px-2 py-1 rounded inline-block mt-1 ${
                        order.shippingStatus === "cancelled"
                          ? "bg-red-100 text-red-600"
                          : order.shippingStatus === "in_transit"
                          ? "bg-blue-100 text-blue-600"
                          : order.shippingStatus === "shipped"
                          ? "bg-green-100 text-green-600"
                          : "bg-yellow-100 text-yellow-600"
                      }`}
                    >
                      {order.shippingStatus}
                    </div>
                  </td>

                  {/* AWB / Courier */}
                  <td className="p-4">
                    <p className="font-medium">{order.awbNumber}</p>
                    <p>{order.courier}</p>
                  </td>

                  {/* Payment / Invoice */}
                  <td className="p-4">
                    ₹{order.total_amount}
                    <div className="text-xs mt-1 bg-green-100 text-green-700 inline-block px-2 py-1 rounded">
                      {order.PaymentType}
                    </div>
                  </td>

                  {/* Products */}
                  <td className="p-4">
                    {order.ProductDetails?.map((prod, i) => (
                      <p key={i} className="truncate">
                        {prod.name} (Qty: {prod.QTY})
                      </p>
                    ))}
                  </td>

                  {/* Customer / Contact */}
                  <td className="p-4">
                    <p>{order.CustomerName}</p>
                    <p>{order.BillingAddress?.phone}</p>
                  </td>

                  {/* Shipping Address */}
                  <td className="p-4 text-sm">
                    <p>{order.ShippingAddress?.address_line1}</p>
                    {order.ShippingAddress?.address_line2 && (
                      <p>{order.ShippingAddress.address_line2}</p>
                    )}
                    <p>
                      {order.ShippingAddress?.city},{" "}
                      {order.ShippingAddress?.state} -{" "}
                      {order.ShippingAddress?.pincode}
                    </p>
                    <p>{order.ShippingAddress?.country}</p>
                  </td>

                  {/* RTO Info */}
                  <td className="p-4 text-sm">
                    {order.rtoCaption && (
                      <p>
                        <strong>Caption:</strong> {order.rtoCaption}
                      </p>
                    )}
                    {order.rtoPhoto && (
                      <p>
                        <strong>Photo:</strong>{" "}
                        <a
                          href={order.rtoPhoto}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View
                        </a>
                      </p>
                    )}
                    {order.rtoRemark && (
                      <p>
                        <strong>Remark:</strong> {order.rtoRemark}
                      </p>
                    )}
                    {!order.rtoCaption &&
                      !order.rtoPhoto &&
                      !order.rtoRemark && (
                        <span className="text-gray-400 text-xs">
                          No RTO info
                        </span>
                      )}
                  </td>

                  {/* Actions */}
                  {rtoStatus === "" && (
                    <td className="p-4 space-x-2">
                      {order.rtoStatus !== "accepted" &&
                      order.rtoStatus !== "rejected" ? (
                        <>
                          {/* Accept Button */}
                          <button
                            onClick={() => handleAccept(order._id)}
                            className="px-3 py-1 rounded-lg bg-primaryLight hover:bg-primary text-primary border border-primary hover:text-white transition-colors"
                          >
                            Accept
                          </button>

                          {/* Reject Button */}
                          <button
                            onClick={() => handleRejectOpen(order._id)}
                            className="px-3 py-1 rounded-lg text-red-600 border border-red-600 bg-primaryLight hover:bg-red-600 hover:text-white hover:border-red-700 transition-colors"
                          >
                            Reject
                          </button>
                        </>
                      ) : (
                        <span
                          className={`text-xs font-semibold ${
                            order.rtoStatus === "accepted"
                              ? "text-green-600"
                              : order.rtoStatus === "rejected"
                              ? "text-red-600"
                              : "text-gray-500"
                          }`}
                        >
                          {order.rtoStatus}
                        </span>
                      )}
                    </td>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={11} className="text-center py-6 text-gray-400">
                  No orders found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Reject Modal */}
      <Modal
        title="Reject RTO Order"
        open={rejectModalOpen}
        onCancel={handleModalClose}
        footer={[
          <Button key="cancel" onClick={handleModalClose}>
            Cancel
          </Button>,
          <Button
            key="reject"
            danger
            type="primary"
            onClick={handleRejectConfirm}
            disabled={!rejectRemark}
          >
            Reject
          </Button>,
        ]}
      >
        <div className="space-y-4">
          <div>
            <p className="mb-2 font-medium">Select rejection reason:</p>
            <Select
              className="w-full"
              placeholder="Select a reason"
              value={rejectRemark || undefined}
              onChange={(value) => setRejectRemark(value)}
              options={RTO_REJECT_REASONS.map((reason) => ({
                label: reason,
                value: reason,
              }))}
            />
          </div>

          <div>
            <p className="mb-2 font-medium">Upload photo (optional):</p>
            <Upload
              beforeUpload={(file) => {
                setRejectFile(file);
                return false;
              }}
              maxCount={1}
              onRemove={() => setRejectFile(null)}
            >
              <Button icon={<UploadOutlined />}>Upload Photo</Button>
            </Upload>
            {rejectFile && (
              <p className="mt-2 text-sm text-gray-600">
                Selected file: {rejectFile.name}
              </p>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default RtoStatus;